import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { databaseExportAPI } from '../api';
import { Database, Download, HardDrive, FileJson, Calendar, AlertCircle } from 'lucide-react';

const AdminDatabaseExport = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await databaseExportAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      setError('İstatistikler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setError(null);

      // Get export data
      const response = await databaseExportAPI.exportDatabase();
      const exportData = response.data;

      // Create JSON file
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `cinefora_backup_${timestamp}.json`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert('✅ Veritabanı başarıyla export edildi!');
    } catch (error) {
      console.error('Export error:', error);
      setError(error.response?.data?.detail || 'Export sırasında hata oluştu');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-white">Yükleniyor...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Database className="w-8 h-8 text-[#FDB714]" />
              Veritabanı Yedekleme
            </h1>
            <p className="text-gray-400 mt-2">
              Canlı veritabanınızın tam yedeğini alın
            </p>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Card className="bg-red-900/20 border-red-700 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-red-400 font-semibold">Hata</h3>
                <p className="text-red-300 text-sm mt-1">{error}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Stats Card */}
        {stats && (
          <Card className="bg-gray-900 border-gray-800 p-6">
            <div className="flex items-center gap-3 mb-6">
              <HardDrive className="w-6 h-6 text-[#FDB714]" />
              <h2 className="text-xl font-bold text-white">Veritabanı İstatistikleri</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="text-gray-400 text-sm mb-1">Veritabanı</div>
                <div className="text-2xl font-bold text-white">{stats.database_name}</div>
              </div>
              
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="text-gray-400 text-sm mb-1">Toplam Collection</div>
                <div className="text-2xl font-bold text-[#FDB714]">{stats.total_collections}</div>
              </div>
              
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="text-gray-400 text-sm mb-1">Toplam Kayıt</div>
                <div className="text-2xl font-bold text-green-400">{stats.total_documents}</div>
              </div>
            </div>

            {/* Collections List */}
            <div className="space-y-2">
              <h3 className="text-white font-semibold mb-3">Collections:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {stats.collections.map((collection) => (
                  <div
                    key={collection.name}
                    className="flex items-center justify-between bg-gray-800 rounded px-4 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <FileJson className="w-4 h-4 text-gray-400" />
                      <span className="text-white text-sm">{collection.name}</span>
                    </div>
                    <span className="text-gray-400 text-sm font-mono">
                      {collection.count} kayıt
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Export Card */}
        <Card className="bg-gradient-to-br from-yellow-900/20 to-orange-900/20 border-yellow-700/50 p-6">
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="bg-yellow-500/20 rounded-lg p-3">
                <Download className="w-8 h-8 text-yellow-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-2">
                  Veritabanını Export Et
                </h3>
                <p className="text-gray-300 text-sm mb-4">
                  Tüm veritabanı JSON formatında export edilecek. Bu dosyayı VPS'e taşımak için kullanabilirsiniz.
                </p>
                
                <div className="bg-gray-900/50 rounded-lg p-4 mb-4">
                  <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Export İçeriği:
                  </h4>
                  <ul className="text-gray-300 text-sm space-y-1 ml-6">
                    <li>✅ Tüm filmler ve detayları</li>
                    <li>✅ Tüm seanslar</li>
                    <li>✅ Salon/şube bilgileri</li>
                    <li>✅ Kampanyalar ve haberler</li>
                    <li>✅ Admin kullanıcıları</li>
                    <li>✅ Site ayarları</li>
                    <li>✅ Tüm diğer veriler</li>
                  </ul>
                </div>

                <Button
                  onClick={handleExport}
                  disabled={exporting}
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-semibold px-8 py-3 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-black border-t-transparent mr-2"></div>
                      Export Ediliyor...
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5 mr-2" />
                      Veritabanını Export Et
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Info Card */}
        <Card className="bg-blue-900/20 border-blue-700/50 p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-blue-400 font-semibold mb-2">VPS'e Taşıma İçin</h3>
              <ol className="text-gray-300 text-sm space-y-2 ml-4 list-decimal">
                <li>
                  <strong>Export Et:</strong> Yukarıdaki butona tıklayarak JSON dosyasını indirin
                </li>
                <li>
                  <strong>VPS'e Yükle:</strong> JSON dosyasını VPS sunucunuza transfer edin
                </li>
                <li>
                  <strong>Import Et:</strong> MongoDB'ye import edin:
                  <pre className="bg-gray-900 rounded px-2 py-1 mt-1 text-xs overflow-x-auto">
                    mongoimport --db=cinefora_db --collection=movies --file=cinefora_backup.json
                  </pre>
                </li>
              </ol>
              <p className="text-gray-400 text-xs mt-3">
                💡 <strong>Not:</strong> Export edilen dosya, şu anki canlı veritabanının tam bir kopyasıdır. 
                Tüm güncel verilerinizi içerir.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDatabaseExport;
